import * as React from 'react';
import * as _ from 'lodash';
import { hashHistory } from 'react-router';
import { DataShape, Field, FormProps } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';

import { ActionButton, Heading, media, mixins, TitleCard } from 'consumer-components';
import { diagnostics, inputFormatters, utils } from 'ui-common';

import * as journeyChooserAPI from 'login-apis/journeyChooserAPI';
import * as loansAPI from 'login-apis/loansAPI';

import BorderlessIconButton, { BorderlessIconButtonConfig } from 'login-components/Common/Buttons/BorderlessIconButton';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import FoundSVG from 'login-components/Common/svg/FoundSVG';
import InputMap from 'login-components/Common/Inputs/InputMap';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import LookingSVG from 'login-components/Common/svg/LookingSVG';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import RefinanceSVG from 'login-components/Common/svg/RefinanceSVG';
import { AcceptedTermsTypes } from 'login-components/Common/Inputs/RequirementInput/utils';
import RequiredViewingRequirementInput from 'login-components/Common/Inputs/RequirementInput/RequiredViewingRequirementInputContainer';
import ThinkingSVG from 'login-components/Common/svg/ThinkingSVG';
import TransitioningFields from 'login-components/Common/TransitioningFields/TransitioningFields';
import States from 'login-constants/states';

import { get as getDeploymentConfig, getOrganizationColors } from 'login-utils/deploymentConfig';
import * as onboardingForm from 'login-utils/onboardingForm';
import * as globalWrappers from 'login-utils/globalWrappers';

const { featureFlags } = utils;

const timelineChooserConfigs: BorderlessIconButtonConfig[] = [
  {
    value: 'NOT_STARTED',
    IconComponent: ThinkingSVG,
    buttonId: 'it-journeyChooserNotStarted',
    text: "I'm just thinking about buying",
  },
  {
    value: 'NOT_IN_CONTRACT',
    IconComponent: LookingSVG,
    buttonId: 'it-journeyChooserNotInContract',
    text: "I'm making offers",
  },
  {
    value: 'FOUND',
    IconComponent: FoundSVG,
    buttonId: 'it-journeyChooserFound',
    text: 'I signed a purchase contract',
  },
  {
    value: 'REFINANCE',
    IconComponent: RefinanceSVG,
    buttonId: 'it-journeyChooserRefinance',
    text: 'I want to refinance my home',
  },
];

const requirementInputConsents: AcceptedTermsTypes[] = ['termsOfUse', 'privacyPolicy', 'eConsent', 'creditConsent'];

export function getTimelineChooserConfig(value: onboardingForm.TimelineValue) {
  return _.find(timelineChooserConfigs, { value });
}

export function generateInputs(phoneNumber?: JourneyChooser.Props['phoneNumber']): FormInput[] {
  return _.compact([
    {
      ...InputMap.RequirementInput,
      acceptedTermsTypes: ['termsOfUse', 'privacyPolicy', 'eConsent', 'creditConsent'],
      identifier: 'terms & conditions',
      name: 'acceptTermsAndConditions',
      label: 'Terms & Conditions',
    },
    getDeploymentConfig().askForConsentToLenderContact &&
      phoneNumber && {
        ...InputMap.UnformattedRequirementInput,
        displayParts: [
          'I ',
          {
            type: 'openModal',
            display: 'consent to be contacted',
            textScreenId: 'consent-to-lender-contact',
            headerText: 'Consent to Contact',
          },
          ' at ',
          inputFormatters.PHONE.format(phoneNumber),
          '. ',
          {
            display: 'Edit',
            onClick: () => {
              hashHistory.push(`${States.PROVIDE_PHONE_NUMBER}`);
            },
          },
        ],
        identifier: 'consent to be contacted',
        name: 'giveConsentToLenderContact',
      },
  ]);
}

const styles = StyleSheet.create({
  timelineContainer: {
    ...mixins.paddingHorizontal(8),
    [media.smallView]: {
      ...mixins.paddingHorizontal(8),
    },
  },
  cardPadding: {
    ...mixins.padding(24, 24, 32, 24),
  },
  fieldLabel: {
    marginBottom: 16,
  },
  loadingPhoneNumber: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  editButton: {
    ...mixins.paddingVertical(12),
    textAlign: 'center',
  },
  buttonWrapper: {
    overflow: 'auto',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  },
  inputBorderBottom: {
    ':not(:last-child)': {
      paddingBottom: 20,
      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    },
  },
  errorWrapper: {
    paddingTop: 16,
  },
});

namespace JourneyChooser {
  export interface StateProps {
    phoneNumber: string | null;
    selectedTimelineValue: onboardingForm.TimelineValue | null;
  }

  export interface DispatchProps {
    setPhoneNumber: (phoneNumber: string) => void;
    selectTimelineValue: (value: StateProps['selectedTimelineValue']) => void;
  }

  export type OwnFormProps = FormProps<DataShape, {}, ReduxState>;

  export type Props = OwnFormProps & StateProps & DispatchProps & ReactRouter.RouteComponentProps<{}, {}>;

  export interface State {
    visibleTimelineChooserOptions: typeof timelineChooserConfigs;
    isFetchingPhoneNumber: boolean;
    submitButtonDisabled: boolean;
    submitError: string | null;
  }
}

class JourneyChooser extends React.Component<JourneyChooser.Props, JourneyChooser.State> {
  useRequiredViewingInput: boolean;

  constructor(props: JourneyChooser.Props) {
    super(props);
    const existingSelectedTimelineValue = props.selectedTimelineValue;
    this.useRequiredViewingInput = featureFlags.isEnabled('requireViewingTermsBeforeAccepting20190904');
    this.state = {
      visibleTimelineChooserOptions: existingSelectedTimelineValue
        ? [getTimelineChooserConfig(existingSelectedTimelineValue)!]
        : timelineChooserConfigs,
      isFetchingPhoneNumber: false,
      submitButtonDisabled: false,
      submitError: null,
    };
  }

  shouldFetchPhoneNumber = () => {
    const { phoneNumber } = this.props;
    return getDeploymentConfig().askForConsentToLenderContact && !phoneNumber && !this.useRequiredViewingInput;
  };

  async componentDidMount() {
    // If we need to show the borrower their phone number, we fetch the loan asynchronously here and populate
    // the consent to contact input below once we fetch it. If the phone number is already set on the form, which
    // can happen if the borrower edits their phone number, we use that instead
    const { setPhoneNumber } = this.props;
    if (!this.shouldFetchPhoneNumber()) return;
    this.setState({
      isFetchingPhoneNumber: true,
    });
    try {
      const currentLoan = await loansAPI.getCurrentLoan();
      // we assume on this screen that there is only one borrower and we just take the first phone number listed
      const phoneNumber = _.toString(_.get(currentLoan, 'application.borrowers.0.phoneNumbers.0.phoneNumber', ''));
      // set the phone number on the form so that we can access it from redux
      if (phoneNumber) setPhoneNumber(phoneNumber);
    } catch (err) {
      diagnostics.error(err);
    }
    this.setState({
      isFetchingPhoneNumber: false,
    });
  }

  onJourneyChooserSubmit = async values => {
    this.setState({ submitButtonDisabled: true, submitError: null });
    try {
      await journeyChooserAPI.journeyChooserSubmit(values, requirementInputConsents);
      return globalWrappers.setHref('/');
    } catch (err) {
      diagnostics.error(err);
      this.setState({
        submitButtonDisabled: false,
        submitError:
          'We encountered an issue while processing your information. Please refresh the page and try again.',
      });
    }
  };

  onTimelineValueSelect = value => {
    const { selectTimelineValue } = this.props;
    const selectedConfig = getTimelineChooserConfig(value)!;
    selectTimelineValue(value);
    this.setState({
      visibleTimelineChooserOptions: [selectedConfig],
    });
  };

  onTimelineValueUnselect = () => {
    const { selectTimelineValue } = this.props;
    selectTimelineValue(null);
    this.setState({
      visibleTimelineChooserOptions: timelineChooserConfigs,
    });
  };

  getTimelineTransitionConfigs = (): TransitioningFields.FieldConfig[] => {
    const { selectedTimelineValue } = this.props;
    const { visibleTimelineChooserOptions } = this.state;
    const timelineTransitionConfigs = _.map(visibleTimelineChooserOptions, option => {
      const onClick = selectedTimelineValue
        ? _.noop
        : () => {
            this.onTimelineValueSelect(option.value);
          };
      return {
        key: option.value,
        style: 'fadeAndSlide',
        component: (
          <div className={css(styles.buttonWrapper)}>
            <BorderlessIconButton {...option} onClick={onClick} type="button" selectable={!selectedTimelineValue} />
          </div>
        ),
      } as TransitioningFields.FieldConfig;
    });

    const editButtonTransitionConfig: TransitioningFields.FieldConfig = {
      key: 'JourneyChooser-timelineEdit',
      in: !!selectedTimelineValue,
      style: 'fadeAndSlide',
      component: (
        <div className={css(styles.editButton)}>
          <ActionButton
            sizing={'xs'}
            option="secondary"
            type="button"
            onClick={this.onTimelineValueUnselect}
            color={getOrganizationColors().action}
            data-it="JourneyChooser-timelineEdit"
          >
            Edit
          </ActionButton>
        </div>
      ),
    };

    return [...timelineTransitionConfigs, editButtonTransitionConfig];
  };

  renderTermsAndConditions = () => {
    const { phoneNumber } = this.props;
    const { isFetchingPhoneNumber, submitButtonDisabled, submitError } = this.state;

    if (this.useRequiredViewingInput) {
      return (
        <React.Fragment>
          <div className={css(styles.fieldLabel)}>
            <label>
              <Heading
                text={'Terms & Conditions'.toUpperCase()}
                rank={6}
                fontSize={12}
                font="Roboto"
                weight={600}
                color={getOrganizationColors().action}
              />
            </label>
          </div>
          <RequiredViewingRequirementInput
            acceptedTermsTypes={['termsOfUse', 'privacyPolicy', 'eConsent', 'creditConsent']}
            formName={onboardingForm.FORM_NAME}
            submitButtonDisabled={submitButtonDisabled}
            submitError={submitError}
          />
        </React.Fragment>
      );
    }

    const termsAndConditionsInputs = generateInputs(phoneNumber);

    return (
      <React.Fragment>
        {this.shouldFetchPhoneNumber() && isFetchingPhoneNumber ? (
          <div className={css(styles.loadingPhoneNumber, styles.cardPadding)}>
            <LoadingSpinner />
          </div>
        ) : (
          <div data-it="JourneyChooser-termsAndConditionsInputs">
            <div>
              {termsAndConditionsInputs.map(input => (
                <div className={css(paneFormStyles.fieldWrapper, styles.inputBorderBottom)} key={input.name}>
                  <div className={css(styles.fieldLabel)}>
                    {input.label && (
                      <label htmlFor={input.name}>
                        <Heading
                          text={input.label.toUpperCase()}
                          rank={6}
                          fontSize={12}
                          font="Roboto"
                          weight={600}
                          color={getOrganizationColors().action}
                        />
                      </label>
                    )}
                  </div>
                  <Field {..._.omit(input, 'label')} />
                </div>
              ))}
            </div>
            {!this.useRequiredViewingInput && (
              <React.Fragment>
                <div className={css(paneFormStyles.buttonDisplay, paneFormStyles.buttonPadding)}>
                  <PrimaryButton
                    isWide={true}
                    text="Continue"
                    fontFamily="Roboto"
                    data-it="JourneyChooser-continue"
                    disabled={submitButtonDisabled}
                  />
                </div>
                {submitError && (
                  <div className={css(styles.errorWrapper)}>
                    <ErrorMessage errorText={submitError} />
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  render() {
    const { handleSubmit, selectedTimelineValue } = this.props;
    const onSubmit = handleSubmit!(this.onJourneyChooserSubmit);

    return (
      <form
        noValidate
        onSubmit={onSubmit}
        className={css(paneFormStyles.wrapper)}
        data-it="JourneyChooser"
        // Set form name here so that we can submit it using buttons outside the form
        // (i.e. from the RequiredViewingRequirementInput modal)
        id={onboardingForm.FORM_NAME}
      >
        <TitleCard>
          <div className={css(styles.timelineContainer)}>
            <TransitioningFields
              key="timelineFields"
              data-it="JourneyChooser-timelineFields"
              fields={this.getTimelineTransitionConfigs()}
            />
          </div>
        </TitleCard>

        <TransitioningFields
          data-it="JourneyChooser-submitWrapper"
          fields={[
            {
              key: 'JourneyChooser-submitWrapper',
              in: !!selectedTimelineValue,
              style: 'fade',
              component: (
                <div className={css(paneFormStyles.inputsWrapper)}>
                  <TitleCard>
                    <div className={css(styles.cardPadding)}>{this.renderTermsAndConditions()}</div>
                  </TitleCard>
                </div>
              ),
            },
          ]}
        />
      </form>
    );
  }
}

export default JourneyChooser;
